import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { Investigation, InvestigationReadLead } from '@/domains/types'
import { resolveLanguage, type SupportedLangCode } from '@/application/i18n/language'
import { buildSpeechSegments } from '@/application/tts/build-speech-segments'
import { resolveContentLang } from '@/application/tts/content-lang'
import { totalSeconds } from '@/application/tts/listening-time'
import { useReadAloud } from '@/application/hooks/useReadAloud'
import type { TtsEngine } from '@/application/tts/tts-engine'
import { ReadAloudBar } from './ReadAloudBar'

export interface ReadAloudPlayerProps {
  investigation: Investigation
  leads: InvestigationReadLead[]
  availableLocales: SupportedLangCode[]
  /** Test-only injection point for a fake `TtsEngine` — defaults to the
   * shared `WebSpeechEngine` singleton in production. */
  engine?: TtsEngine
  /** Test-only injection point for a fake site-voice (Piper) engine. */
  siteEngine?: TtsEngine
}

/**
 * Owns the single `useReadAloud` instance for the article and renders it
 * twice: inline, right where `ReadArticleHeader` places it, and — only
 * while `playing`/`paused` and the inline bar has scrolled out of view — a
 * second copy sticky-anchored to the bottom of the viewport through a
 * portal, so playback controls stay reachable while scrolling. Renders
 * nothing when the widget is `unsupported` (no Web Speech API, or no voice
 * at all for the article's own language).
 */
export function ReadAloudPlayer({
  investigation,
  leads,
  availableLocales,
  engine,
  siteEngine,
}: ReadAloudPlayerProps) {
  const { i18n } = useTranslation()
  const uiLocale = resolveLanguage(i18n.language)

  const segments = useMemo(
    () => buildSpeechSegments({ investigation, leads, availableLocales }, uiLocale),
    [investigation, leads, availableLocales, uiLocale],
  )

  const primaryLang = resolveContentLang(
    investigation.sourceLocale,
    investigation.sourceLocale,
    uiLocale,
    availableLocales,
  )

  // Deliberately keyed on the resolved content (id + locale + segment
  // shape/length) rather than just `investigation.id:uiLocale`: toggling the
  // preview-mode banner's public/private view re-fetches `leads` with a
  // different set of published facts, which must reset playback exactly
  // like navigating to a different article would.
  const totalWords = segments.reduce((wordsSoFar, segment) => wordsSoFar + segment.words, 0)
  const resetKey = `${investigation.id}:${uiLocale}:${segments.length}:${totalWords}`

  const {
    status,
    segmentIndex,
    segmentCount,
    elapsedSeconds,
    totalSeconds: totalSecondsValue,
    rate,
    voices,
    selectedVoiceId,
    lowQualityOnly,
    toggle,
    stop,
    seekToSegment,
    cycleRate,
    setVoice,
    preparingProgress,
    enginePreference,
    siteAvailable,
    deviceAvailable,
    consentPending,
    usingFallback,
    acceptModelDownload,
    declineModelDownload,
    setEnginePreference,
  } = useReadAloud({
    segments,
    primaryLang,
    resetKey,
    engine,
    siteEngine,
    title: investigation.name,
  })

  // The "resting" duration label is always at 1x, regardless of the
  // visitor's chosen speed — only the active transport bar's time/total
  // reflects the current rate.
  const restingDurationMinutes = useMemo(
    () => Math.max(1, Math.ceil(totalSeconds(segments, 1) / 60)),
    [segments],
  )

  const inlineRef = useRef<HTMLDivElement>(null)
  const [inlineVisible, setInlineVisible] = useState(true)

  useEffect(() => {
    const node = inlineRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(
      ([entry]) => setInlineVisible(entry?.isIntersecting ?? true),
      {
        threshold: 0,
      },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  if (status === 'unsupported') return null

  const showSticky =
    (status === 'playing' || status === 'paused' || (status === 'preparing' && segmentIndex > 0)) &&
    !inlineVisible

  const barProps = {
    status,
    title: investigation.name,
    restingDurationMinutes,
    segmentIndex,
    segmentCount,
    elapsedSeconds,
    totalSeconds: totalSecondsValue,
    rate,
    voices,
    primaryLang,
    selectedVoiceId,
    lowQualityOnly,
    toggle,
    stop,
    seekToSegment,
    cycleRate,
    setVoice,
    preparingProgress,
    enginePreference,
    siteAvailable,
    deviceAvailable,
    consentPending,
    usingFallback,
    acceptModelDownload,
    declineModelDownload,
    setEnginePreference,
  }

  return (
    <>
      <div ref={inlineRef} className="mb-8">
        <ReadAloudBar variant="inline" {...barProps} />
      </div>
      {showSticky &&
        createPortal(
          <div className="read-aloud-sticky-wrapper fixed inset-x-0 z-30 flex justify-center px-4 print:hidden">
            <div className="w-full max-w-[40rem]">
              <ReadAloudBar variant="sticky" {...barProps} />
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
